import { BaseTool } from './base';
import { getEnabledElement } from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';

import { mat4, vec3 } from 'gl-matrix';
import type { EventTypes, PublicToolProps, ToolProps } from '../types';
import type { MouseWheelEventType } from '../types/EventTypes';
import getViewportICamera from '../utilities/getViewportICamera';
import setViewportCamera from '../utilities/setViewportCamera';

const DIRECTIONS = {
  X: [1, 0, 0],
  Y: [0, 1, 0],
  Z: [0, 0, 1],
  CUSTOM: [],
};

/**
 * Tool that rotates the camera on mouse wheel, and on horizontal mouse/touch drag.
 * It rotates the camera around the focal point, and around a defined axis. Default
 * axis is set to be Z axis, but it can be configured to any custom normalized axis.
 *
 */
class VolumeRotateTool extends BaseTool {
  static toolName;
  _configuration: unknown;

  constructor(
    toolProps: PublicToolProps = {},
    defaultToolProps: ToolProps = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        direction: DIRECTIONS.Z,
        rotateIncrementDegrees: 30,
        rotateDragDegreesPerPixel: 0.5,
      },
    }
  ) {
    super(toolProps, defaultToolProps);
  }

  mouseWheelCallback(evt: MouseWheelEventType) {
    // https://github.com/kitware/vtk-js/blob/HEAD/Sources/Interaction/Manipulators/MouseCameraUnicamRotateManipulator/index.js#L73
    const { element, wheel } = evt.detail;
    const { rotateIncrementDegrees } = this.configuration;
    const { direction: deltaY } = wheel;

    //Calculate angle in radian as glmatrix rotate is in radian
    const angle = (deltaY * (rotateIncrementDegrees * Math.PI)) / 180;

    this._rotate(element, angle);
  }

  mouseDragCallback(evt: EventTypes.InteractionEventType) {
    this._dragCallback(evt);
  }

  touchDragCallback(evt: EventTypes.InteractionEventType) {
    this._dragCallback(evt);
  }

  _dragCallback(evt: EventTypes.InteractionEventType) {
    const { element, deltaPoints } = evt.detail;
    const { rotateDragDegreesPerPixel } = this.configuration;
    const deltaX = deltaPoints.canvas[0];

    // High-resolution pointers can fire drag events before a full pixel of
    // horizontal movement has accumulated.
    if (!deltaX) {
      return;
    }

    const angle = (deltaX * rotateDragDegreesPerPixel * Math.PI) / 180;

    this._rotate(element, angle);
  }

  _rotate(element: HTMLDivElement, angle: number) {
    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;
    const { direction } = this.configuration;

    const camera = getViewportICamera(viewport);
    const { viewUp, position, focalPoint } = camera;

    const [cx, cy, cz] = focalPoint;
    const [ax, ay, az] = direction;

    // position[3] = 1.0
    // focalPoint[3] = 1.0
    // viewUp[3] = 0.0

    const newPosition: Types.Point3 = [0, 0, 0];
    const newFocalPoint: Types.Point3 = [0, 0, 0];
    const newViewUp: Types.Point3 = [0, 0, 0];

    const transform = mat4.identity(new Float32Array(16));
    mat4.translate(transform, transform, [cx, cy, cz]);
    mat4.rotate(transform, transform, angle, [ax, ay, az]);
    mat4.translate(transform, transform, [-cx, -cy, -cz]);
    vec3.transformMat4(newPosition, position, transform);
    vec3.transformMat4(newFocalPoint, focalPoint, transform);

    mat4.identity(transform);
    mat4.rotate(transform, transform, angle, [ax, ay, az]);
    vec3.transformMat4(<Types.Point3>newViewUp, viewUp, transform);

    setViewportCamera(viewport, {
      position: newPosition,
      viewUp: newViewUp,
      focalPoint: newFocalPoint,
    });

    viewport.render();
  }
}

VolumeRotateTool.toolName = 'VolumeRotateMouseWheel';
export default VolumeRotateTool;
